import { useState, useEffect } from "react";
import {
    Container,
    Typography,
    Checkbox,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from "@mui/material";
import { Folder as FolderIcon, InsertDriveFile as FileIcon } from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import SuperAdminFolderService from "../../../service/superadmin/folderService";
import { toast } from "react-toastify";
import { Document, Folder } from "../../../types/folderTypes";
import SuperAdminUserService from "../../../service/superadmin/userService";
import CompanyAdminUserService from "../../../service/companyadmin/userService";
import UserFolderService from "../../../service/user/folderService";

const AssignAccess = () => {
    const { userId, companyId } = useParams<{ userId: string; companyId: string }>();
    const navigate = useNavigate();

    const [folders, setFolders] = useState<Folder[]>([]);
    const [unassignedDocuments, setUnassignedDocuments] = useState<Document[]>([]);
    const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
    const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        fetchCompanyContent();
        fetchAssignedAccess();
    }, []);

    const fetchCompanyContent = async () => {
        try {
            const data = await CompanyAdminUserService.getCompanySpaceContent();

            if (data?.folders) {
                setFolders(data.folders);
            }

            if (data?.documents) {
                setUnassignedDocuments(data.documents);
            }
        } catch (error) {
            toast.error("Failed to fetch company content.");
        } finally {
            setLoading(false);
        }
    };

    const fetchAssignedAccess = async () => {
        try {
            const data = await CompanyAdminUserService.getAssignedAccess(Number(userId));

            if (data?.folder_ids) {
                setSelectedFolders(data.folder_ids.map(String));
            }

            if (data?.document_ids) {
                setSelectedDocuments(data.document_ids.map(String));
            }
        } catch (error) {
            toast.error("Failed to fetch assigned access.");
        }
    };

    const handleFolderCheckboxChange = (folderId: string) => {
        setSelectedFolders((prevSelected) =>
            prevSelected.includes(folderId) ? prevSelected.filter((id) => id !== folderId) : [...prevSelected, folderId]
        );
    };

    const handleDocumentCheckboxChange = (documentId: string) => {
        setSelectedDocuments((prevSelected) =>
            prevSelected.includes(documentId) ? prevSelected.filter((id) => id !== documentId) : [...prevSelected, documentId]
        );
    };

    const submitSelectedItems = async () => {
        if (selectedFolders.length === 0 && selectedDocuments.length === 0) {
            toast.error("Please select at least one folder or document.");
            return;
        }

        try {
            await CompanyAdminUserService.assignAccess(Number(userId), {
                folder_ids: selectedFolders.map((id) => Number(id)),
                document_ids: selectedDocuments.map((id) => Number(id)),
            });

            toast.success("Access assigned successfully!");
            navigate("/ca/users"); // Redirect to user list
        } catch (error) {
            toast.error("Failed to assign access.");
        }
    };

    return (
        <Container>
            <Typography variant="h5" sx={{ mt: 3, mb: 2 }}>
                Assign Access to User
            </Typography>

            {loading ? (
                <Typography>Loading...</Typography>
            ) : (
                <>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>#</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Select</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {/* Display Folders First */}
                                {folders.map((folder, index) => (
                                    <TableRow key={folder.id}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>
                                            <FolderIcon sx={{ color: "purple", mr: 1 }} />
                                            {folder.folder_name}
                                        </TableCell>
                                        <TableCell>
                                            <Checkbox
                                                checked={selectedFolders.includes(folder.id.toString())}
                                                onChange={() => handleFolderCheckboxChange(folder.id.toString())}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}

                                {/* Display Documents */}
                                {unassignedDocuments.map((document, index) => (
                                    <TableRow key={document.id}>
                                        <TableCell>{folders.length + index + 1}</TableCell>
                                        <TableCell>
                                            <FileIcon sx={{ color: "blue", mr: 1 }} />
                                            {document.document_name}
                                        </TableCell>
                                        <TableCell>
                                            <Checkbox
                                                checked={selectedDocuments.includes(document.id.toString())}
                                                onChange={() => handleDocumentCheckboxChange(document.id.toString())}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Submit Button */}
                    <Button variant="contained" color="primary" onClick={submitSelectedItems} sx={{ mt: 3 }}>
                        Submit Access
                    </Button>
                </>
            )}
        </Container>
    );
};

export default AssignAccess;
